import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import config from "../../config";
import { db } from "../../db";
import { formatDate } from "../../utils/patientUtils";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  Fab,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UploadIcon from '@mui/icons-material/Upload';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PatientDetailsComponent from "../patientDetailsComponent/patientDetailsComponent";
import InferenceDetailsComponent from "../inferenceDetailsComponent/inferenceDetailsComponent";
import LoadingComponent from "../loadingComponent/loadingComponent";
import HealthDetailsComponent from "../healthDetailsComponent/healthDetailsComponent";
import DisclaimerComponent from "../disclaimerComponent/disclaimerComponent";
import { nextSteps, handleSetDR, handleSetReferral, getInferenceFromData } from "../../utlis";
import ReportButton from "../ReportButton/ReportButton";
import ExportDialog from "../ExportReportModal/ExportDialog";
import SubmitFeedbackModal from "./SubmitFeedbackModal";
import BackNavigationModal from "./BackNavigationModal";

/** S3 / presigned URLs must be fetched without extra headers — Authorization breaks the signature (400). */
function shouldAttachApiTokenToImageFetch(url) {
  if (!url || typeof url !== 'string') return false;
  if (url.startsWith('blob:') || url.startsWith('data:')) return false;
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : undefined);
    const h = u.hostname.toLowerCase();
    if (h.includes('amazonaws.com') || h.endsWith('.s3.amazonaws.com') || /^[^.]+\.s3\.[^.]+\.amazonaws\.com$/i.test(h)) {
      return false;
    }
    if (u.searchParams.has('X-Amz-Algorithm') || u.searchParams.has('X-Amz-Credential')) {
      return false;
    }
    return true;
  } catch {
    return true;
  }
}

/** Single network read per remote URL → thumbnail object URL. Avoids <img src=https://s3…> + fetch() duplication. */
async function fetchThumbnail(url) {
  if (!url || typeof url !== 'string') return null;
  if (url.startsWith('data:image/')) return url;
  try {
    let blob;
    if (url.startsWith('blob:')) {
      const r = await fetch(url);
      if (!r.ok) return null;
      blob = await r.blob();
    } else {
      const fetchOpts = {};
      if (shouldAttachApiTokenToImageFetch(url)) {
        fetchOpts.headers = {
          Authorization: `Token ${localStorage.getItem('optha-token')}`,
        };
      }
      const response = await fetch(url, fetchOpts);
      if (!response.ok) return null;
      blob = await response.blob();
    }
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error loading report image:', error);
    return null;
  }
}

function revokeIfBlobObjectUrl(u) {
  if (u && typeof u === 'string' && u.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(u);
    } catch (_e) {
      /* ignore */
    }
  }
}

const ReportsComponent = ({ userData }) => {
  const navigate = useNavigate();
  const { patientrecordId } = useParams();
  const header = {
    Authorization: "Token " + localStorage.getItem("optha-token"),
  };
  const [reportData, setReportData] = useState({});
  const [patientData, setPatientData] = useState({});
  const [docData, setDocData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  /** True while the report JSON has loaded but the fundus photo thumbnails are still being
   *  fetched - keeps the loading spinner up so the page doesn't briefly show "single image
   *  mode" text for an eye that actually has a photo, just not downloaded yet. */
  const [imagesLoading, setImagesLoading] = useState(false);
  const [images, setImages] = useState([]);
  /** Thumbnails for InferenceDetails: blob: URLs from fetchThumbnail — never raw S3 here (extra img fetch). */
  const [thumbnailUrls, setThumbnailUrls] = useState({ left: null, right: null });
  const [obj, setObj] = useState();
  const [reports, setReports] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const currentReportIndex = (reports.findIndex(r => (r.uuid === patientrecordId) || (r.id === patientrecordId)))+1;
  const ref = useRef();
  const inferenceRef = useRef();
  const [optometristRole, setOptometristRole] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showBackModal, setShowBackModal] = useState(false);
  /** Mirrors InferenceDetailsComponent's live validation (e.g. "correct diagnosis" required
   *  once an eye is marked incorrect) so the Submit button can be disabled proactively. */
  const [canSubmitFeedback, setCanSubmitFeedback] = useState(true);
  const [offline, setOffline] = useState(false);
  const [isPendingSync, setIsPendingSync] = useState(false);

  // Prefer the ophthalmologist's corrected DME over the raw AI grade - the same
  // feedback-corrected-first pattern already used for DR (left/right_eye_correct_inference).
  const getFinalDME = (eye) =>
    reportData?.feedback_data?.[`${eye}_eye_correct_dme`] || reportData?.ml_model?.model_outputs?.[eye]?.drdme?.DME_grade || null;

  useEffect(() => {
    // Get the optometrist role from localStorage
    const role = localStorage.getItem("optometrist_role");
    setOptometristRole(role);
    
    // Ensure report_id in localStorage matches the URL parameter
    // This helps maintain state on refresh
    if (patientrecordId) {
      localStorage.setItem("report_id", patientrecordId);
    }
  }, [patientrecordId]);

  // Check offline status
  useEffect(() => {
    const checkConnectionStatus = async () => {
      const { isOffline } = await import('../../utils/offlineDetection');
      const offlineStatus = await isOffline();
      setOffline(offlineStatus);
    };

    // Initial check
    checkConnectionStatus();
    
    // Check periodically (every 10 seconds)
    const interval = setInterval(checkConnectionStatus, 10000);
    
    // Listen to browser events for immediate updates
    const handleOnline = async () => {
      const { clearOfflineCache } = await import('../../utils/offlineDetection');
      clearOfflineCache();
      await checkConnectionStatus();
    };
    
    const handleOffline = () => {
      setOffline(true);
      // Verify with API check
      checkConnectionStatus();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      clearInterval(interval);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    // Call handleAPICall when both optometristRole is set and patientrecordId is available
    if (optometristRole !== null && patientrecordId) {
      handleAPICall();
    }
  }, [patientrecordId, optometristRole]);

  const handleAPICall = async () => {
    setIsLoading(true);
    try {
      // First, try to load from IndexDB (for both offline and unsynced data)
      const indexDBReport = await db.reports.where('id').equals(patientrecordId).first();
      
      if (indexDBReport) {
        // Data exists in IndexDB - load it regardless of online/offline status
        await loadDataFromIndexDB();
      } else if (!offline) {
        // Data not in IndexDB and we're online - try API
        await loadDataFromAPI();
      } else {
        // Data not in IndexDB and we're offline
        alert("Report not found in offline storage.");
        navigate("/patientrecord");
      }
    } catch (error) {
      console.log(error);
      alert("An error occurred while fetching data.");
    } finally {
      setIsLoading(false);
    }
  };

  const loadDataFromIndexDB = async () => {
    try {
      // Find the report in IndexDB using the ID
      const report = await db.reports.where('id').equals(patientrecordId).first();
      
      if (!report) {
        alert("Report not found in offline storage.");
        navigate("/patientrecord");
        return;
      }

      // Find the associated patient
      const patient = await db.patients.where('id').equals(report.patient_id).first();
      
      if (!patient) {
        alert("Patient data not found in offline storage.");
        navigate("/patientrecord");
        return;
      }

      // Safari-specific fix: Convert ArrayBuffer to data URLs immediately to avoid blob URL invalidation
      
      const convertArrayBufferToDataURL = async (imageData) => {
        if (!imageData) return null;
        if (imageData.data) {
          // New format: ArrayBuffer stored in IndexedDB
          const blob = new Blob([imageData.data], { type: imageData.type });
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error('Failed to convert ArrayBuffer to data URL'));
            reader.readAsDataURL(blob);
          });
        } else {
          // Legacy format: Blob stored in IndexedDB
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error('Failed to convert blob to data URL'));
            reader.readAsDataURL(imageData);
          });
        }
      };
      
      const leftDataURL = report.left_image ? await convertArrayBufferToDataURL(report.left_image) : null;
      const rightDataURL = report.right_image ? await convertArrayBufferToDataURL(report.right_image) : null;
      
      
      const transformedReport = {
        ...report,
        patient: patient,
        doctor: {
          name: localStorage.getItem("user_name") || "Unknown",
          id: localStorage.getItem("user_id") || 0
        },
        files: {
          left: leftDataURL,
          right: rightDataURL
        },
        // Ensure single_image_reason is preserved
        single_image_reason: report.single_image_reason
      };
      
      setReportData(transformedReport);
      setPatientData(patient);
      setDocData(transformedReport.doctor);
      
      // Check if report is pending sync (from sync page)
      setIsPendingSync(report.syncStatus === 'pending');
      
      // Handle images - use data URLs instead of blob URLs for Safari
      if (report.left_image && report.right_image) {
        setImages([leftDataURL, rightDataURL]);
      }
      
      // Set reports array for ReportButton component
      setReports([{
        uuid: transformedReport.uuid || transformedReport.id,
        visited_at: transformedReport.created_at ? new Date(transformedReport.created_at).toISOString() : new Date().toISOString(),
      }]);
    } catch (error) {
      console.error("Error loading data from IndexDB:", error);
      alert("Error loading offline data.");
    }
  };

  const loadDataFromAPI = async () => {
    try {
      let url = `${config.API_BASE_URL}/api/reports/getreport/?uuid=${patientrecordId}`;

      const reportResponse = await axios.get(url, { headers: header });

      if (!reportResponse.data.results || reportResponse.data.results.length === 0) {
        alert("Data not found");
        return;
      }

      const report = reportResponse.data.results[0];
      
      
      // Ensure the files property is properly structured for the conversion logic
      const reportWithFiles = {
        ...report,
        files: {
          left: report.files?.left || null,
          right: report.files?.right || null
        }
      };
      
      
      setReportData(reportWithFiles);
      setPatientData(report.patient);
      setDocData(report.doctor);
      setIsPendingSync(false); // API data is already synced
      // Do not call imagetoBase64 here: InferenceDetailsComponent already loads each file URL via
      // <img src>, and the thumbnail useEffect below fetches each image once for the thumbnail —
      // a third redundant fetch per image here would just add more rows (+ Service Worker doubling).
    } catch (error) {
      console.error("Error loading data from API:", error);
      throw error;
    }
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  /* Thumbnails for InferenceDetails: blob: URLs from fetchThumbnail — not reportData.files directly (avoids duplicate S3/img load). */
  useEffect(() => {
    let cancelled = false;
    const revokeList = [];

    (async () => {
      if (!reportData?.files) {
        if (!cancelled) {
          setThumbnailUrls({ left: null, right: null });
        }
        return;
      }
      setImagesLoading(true);
      try {
        const left = await fetchThumbnail(reportData.files.left);
        if (cancelled) {
          revokeIfBlobObjectUrl(left);
          return;
        }
        const right = await fetchThumbnail(reportData.files.right);
        if (cancelled) {
          revokeIfBlobObjectUrl(left);
          revokeIfBlobObjectUrl(right);
          return;
        }
        if (left?.startsWith('blob:')) revokeList.push(left);
        if (right?.startsWith('blob:')) revokeList.push(right);
        setThumbnailUrls({ left, right });
      } catch {
        if (!cancelled) {
          setThumbnailUrls({ left: null, right: null });
        }
      } finally {
        if (!cancelled) {
          setImagesLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      revokeList.forEach(revokeIfBlobObjectUrl);
    };
  }, [patientrecordId, reportData?.files?.left, reportData?.files?.right]);

  useEffect(() => {
    if (!reportData?.patient?.id) return;
    const fetchReportsUUId = async () => {
      try {
        if (!patientData || Object.keys(patientData).length === 0) {
          console.error("patientData Not Found");
          return;
        }

        // Check if we're dealing with IndexDB data
        const indexDBReport = await db.reports.where('id').equals(patientrecordId).first();
        
        if (indexDBReport) {
          // Data is from IndexDB - don't overwrite reports array
          return;
        } else if (!offline) {
          // Data not in IndexDB and we're online - try API
          await loadReportsFromAPI();
        }
      } catch (error) {
        console.error("Error fetching reports:", error);
      }
    };
    
    fetchReportsUUId();
  }, [patientData, optometristRole, offline, patientrecordId]);

  const loadReportsFromIndexDB = async () => {
    try {
      // Get all reports for this patient from IndexDB
      const patientReports = await db.reports.where('patient_id').equals(patientData.id).toArray();
      
      if (!patientReports || patientReports.length === 0) {
        return;
      }

      // Transform reports to match API format
      const transformedReports = patientReports.map(report => ({
        uuid: report.uuid || report.id, // Use uuid if available, otherwise use id
        visited_at: report.created_at ? new Date(report.created_at).toISOString() : new Date().toISOString(),
        // Add other fields as needed
      }));

      setReports(transformedReports);
    } catch (error) {
      console.error("Error loading reports from IndexDB:", error);
    }
  };

  const loadReportsFromAPI = async () => {
    try {
      const userProfile = JSON.parse(localStorage.getItem('user_profile'));
      const healthFacilityId = userProfile?.health_facility?.id;
  
      const queryParams = new URLSearchParams();
      queryParams.append('other_id', patientData?.other_id);
      queryParams.append('patient_id', patientData?.id);
      
      // Add health_facility_id if it exists
      if (healthFacilityId) {
        queryParams.append('health_facility_id', healthFacilityId);
      }
  
      const reportResponse = await axios.get(
        `${config.API_BASE_URL}/api/reports/getreports/uuids/?${queryParams.toString()}`,
        { headers: header }
      );

      if (!reportResponse.data || reportResponse.data.length === 0) {
        alert("Data not found");
        return;
      }
      const reportsUuid = reportResponse.data;
      setReports(reportsUuid);
    } catch (error) {
      console.error("Error loading reports from API:", error);
      throw error;
    }
  };

  const handleBackClick = () => {
    // Check if user is Ophthalmologist and hasn't submitted feedback yet
    if (optometristRole === "Ophthalmologist" && !reportData?.feedback_data) {
      // Show the back navigation modal for Ophthalmologists when no feedback exists
      // This reminds them to provide feedback before leaving
      setShowBackModal(true);
      return;
    }
    
    // Check if we came from Sync Data page (offline data)
    if (offline || reportData.id) {
      // Navigate back to Sync Data page
      navigate("/syncdata");
    } else {
      // Navigate back to Patient Records page
      navigate("/patientrecord");
    }
  };
  // Cleanup blob URLs when component unmounts or images change
  useEffect(() => {
    return () => {
      // Revoke all blob URLs when component unmounts
      images.forEach(url => {
        if (url && url.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(url);
          } catch (error) {
            console.warn('Error revoking blob URL:', error);
          }
        }
      });
    };
  }, [images]);

  const handleSubmit = () => {
    // Get form data from inference component to validate
    const formData = inferenceRef.current.getFormData();
    
    // Get inference data to check if eyes have "Image Not Available"
    // Always use AI inference for validation, not feedback data
    const leftInference = handleSetDR(
      reportData?.ml_model?.classification?.left,
      reportData.ml_model?.gradability?.left
    );
    const rightInference = handleSetDR(
      reportData?.ml_model?.classification?.right,
      reportData?.ml_model?.gradability?.right,
      reportData?.ml_model?.model_outputs?.right
    );
    
    // Helper function to check if inference is "Image Not Available"
    const isImageNotAvailable = (inference) => {
      return inference?.includes("Image Not Available") || 
             inference === undefined || 
             inference === null ||
             inference === "";
    };
    
    // Validation: Check if AI inference correction radio buttons are selected
    // Only require feedback for eyes that don't have "Image Not Available"
    if (!isImageNotAvailable(rightInference) && !formData.rightEyeCorrect) {
      alert("Please select whether the AI inference is correct for the right eye.");
      return;
    }
    
    if (!isImageNotAvailable(leftInference) && !formData.leftEyeCorrect) {
      alert("Please select whether the AI inference is correct for the left eye.");
      return;
    }
    
    // Validation: Check if user selected "No" but didn't select correction option
    // Only validate for eyes that don't have "Image Not Available"
    if (!isImageNotAvailable(rightInference) && formData.rightEyeCorrect === 'no' && !formData.rightEyeCorrectedInference) {
      alert("Please select the correct inference for the right eye before submitting.");
      return;
    }
    
    if (!isImageNotAvailable(leftInference) && formData.leftEyeCorrect === 'no' && !formData.leftEyeCorrectedInference) {
      alert("Please select the correct inference for the left eye before submitting.");
      return;
    }
    
    // If validation passes, show confirmation modal
    setShowSubmitModal(true);
  };

  const confirmSubmit = async () => {
    try {
      // Get form data from inference component
      const formData = inferenceRef.current.getFormData();
      
      // Determine which inferences to use for next steps calculation
      const leftInferenceForNextSteps = formData.leftEyeCorrectedInference || handleSetDR(
        reportData?.ml_model?.classification?.left,
        reportData.ml_model?.gradability?.left
      );
      const rightInferenceForNextSteps = formData.rightEyeCorrectedInference || handleSetDR(
        reportData?.ml_model?.classification?.right,
        reportData?.ml_model?.gradability?.right,
        reportData?.ml_model?.model_outputs?.right
      );
      
      // Get DME information for both eyes - prefer the clinician's just-entered correction
      // (mirrors the leftEyeCorrectedInference/rightEyeCorrectedInference fallback above)
      const leftDME = formData.leftEyeCorrectedDME || reportData?.ml_model?.model_outputs?.left?.drdme?.DME_grade || null;
      const rightDME = formData.rightEyeCorrectedDME || reportData?.ml_model?.model_outputs?.right?.drdme?.DME_grade || null;
      
      // Get operator override information
      const operatorOverride = reportData?.ml_model?.operator_override || { left: { fundus: false, eye: false }, right: { fundus: false, eye: false } };
      
      // Calculate next steps using the appropriate inferences and DME information
      const { date, message } = nextSteps(leftInferenceForNextSteps, rightInferenceForNextSteps, leftDME, rightDME, operatorOverride);
      const nextStep = `${message} on ${date}`;
      // Prepare API payload
      const payload = {
        left_eye_inference_is_correct: formData.leftEyeCorrect,
        right_eye_inference_is_correct: formData.rightEyeCorrect,
        next_steps_date: date,
        next_steps_message: nextStep,
      };

      // Add corrected inferences only if "no" was selected
      if (formData.leftEyeCorrect === 'no' && formData.leftEyeCorrectedInference) {
        payload.left_eye_correct_inference = formData.leftEyeCorrectedInference;
      }

      if (formData.rightEyeCorrect === 'no' && formData.rightEyeCorrectedInference) {
        payload.right_eye_correct_inference = formData.rightEyeCorrectedInference;
      }

      // DME confirmation - only present when a DME badge was shown for that eye
      if (formData.leftEyeDMECorrect) {
        payload.left_eye_dme_is_correct = formData.leftEyeDMECorrect;
        if (formData.leftEyeDMECorrect === 'no' && formData.leftEyeCorrectedDME) {
          payload.left_eye_correct_dme = formData.leftEyeCorrectedDME;
        }
      }

      if (formData.rightEyeDMECorrect) {
        payload.right_eye_dme_is_correct = formData.rightEyeDMECorrect;
        if (formData.rightEyeDMECorrect === 'no' && formData.rightEyeCorrectedDME) {
          payload.right_eye_correct_dme = formData.rightEyeCorrectedDME;
        }
      }

      // Add optional fields only if they have values
      if (formData.serviceProvided) {
        payload.treatment_provided = formData.serviceProvided;
      }

      if (formData.furtherReferralRequired) {
        payload.further_referral_required = formData.furtherReferralRequired;
      }


      // Make API call to submit feedback
      const response = await axios.post(
        `${config.API_BASE_URL}/api/reports/feedback/${patientrecordId}/`,
        payload,
        { headers: header }
      );
      
      setShowSubmitModal(false);
      // Navigate back to list so updated data is shown (no full page reload)
      navigate("/patientrecord");
      
    } catch (error) {
      console.error("Error submitting assessment:", error);
      alert("Error submitting assessment. Please try again.");
    }
  };

  const cancelSubmit = () => {
    setShowSubmitModal(false);
  };

  const handleExitWithoutFeedback = () => {
    // Close the modal and navigate back without saving feedback
    setShowBackModal(false);
    navigate("/patientrecord");
  };

  const handleCloseBackModal = () => {
    // Just close the modal and stay on the current page
    setShowBackModal(false);
  };

  const handleImageEditClick = (eye = null) => {
    // Navigate to image upload form for editing images
    // Pass the current report data and eye information
    const editParams = new URLSearchParams({
      edit: 'true',
      reportId: patientrecordId,
      eye: eye || 'both',
      step: '1' // Go to image upload step
    });
    
    navigate(`/patientscreen?${editParams.toString()}`);
  };

  const handlePatientEditClick = () => {
    // Navigate to patient form for editing patient details
    const editParams = new URLSearchParams({
      edit: 'true',
      reportId: patientrecordId,
      step: '0' // Go to patient details step
    });
    
    navigate(`/patientscreen?${editParams.toString()}`);
  };

  return (
    <div>
      <div>
        <Stack
          direction="row"
          padding="10px 5%"
          justifyContent="space-between"
          alignItems="center"
        >
          <IconButton onClick={handleBackClick}>
            <ArrowBackIcon />&nbsp;{!isMobile&&<Typography fontWeight={600} fontSize="20px">Patient Record</Typography>}
          </IconButton>
          <div>
            <Button
              startIcon={<UploadIcon />}
              sx={{
                borderRadius: "8px",
                border: "2px solid var(--Blue, #0D1E83)",
                background: "var(--Blue, #0D1E83)",
                color: "#FFFFFF",
                "&:hover": {
                  background: "var(--Blue, #0D1E83)",
                },
                textTransform: 'none',
              }}
              onClick={() => setOpenDialog(true)}>
              Export Report
            </Button>
            <ExportDialog Open={openDialog} close={() => setOpenDialog(false)} reports={reports}/>
          </div>
        </Stack>
      </div>
      <Divider />
      <div style={{ padding: isMobile ? "0%" : "0% 12.5%" }}>
      <Box
        sx={{
          margin: "16px 16px"
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {`Report ${currentReportIndex}`}
            </Typography>
            {isPendingSync && (
              <Box
                component="span"
                sx={{
                  px: 1.25,
                  py: 0.375,
                  borderRadius: "16px",
                  backgroundColor: "rgba(194, 142, 80, 0.25)",
                  color: "#8B5A2B",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                }}
              >
                Un-Synced
              </Box>
            )}
          </Box>
          {/* Visit date not in header; shown once in patient details card below */}
        </div>
      </Box>
      </div>
      <div style={{ padding: isMobile ? "0%" : "0% 12.5%" }}>
        <Box
          sx={{
            border: "1px solid var(--BoxStroke, rgba(0, 0, 0, 0.05))",
            borderRadius: "8px",
            background: "var(--White, #FFF)",
            boxShadow:
              "0px 2px 4px 0px rgba(0, 0, 0, 0.08), 0px 0px 6px 0px rgba(0, 0, 0, 0.02)",
          }}
        >
          <Box width="100%" sx={{ background: "rgba(189, 215, 242, 0.14)", padding: "15px 0px 15px 0px" }}>
            <PatientDetailsComponent
              edit={(offline || reportData.id) && !isPendingSync} // Disable edit for pending sync records
              handlePatientDetailsEditClick={handlePatientEditClick}
              pat_name={patientData.name}
              pat_age={patientData.age}
              pat_sex={patientData.sex}
              visitDate={formatDate(reportData.created_at || reportData.createdAt)}
              pat_id={patientData.other_id || patientData.abha_id}
              visitNumber={offline || reportData.id ? (currentReportIndex || 1) : currentReportIndex}
              // pat_phone={patientData.phone}
              // docName={docData?.user?.name}
              pat_diabetes={reportData.diabetes}
              diabetes_duration_years={reportData.diabetes_duration_years}
              diabetes_duration_months={reportData.diabetes_duration_months}
              visual_acuity_distant_left={reportData.visual_acuity_distant_left}
              visual_acuity_distant_right={reportData.visual_acuity_distant_right}
              visual_acuity_near_left={reportData.visual_acuity_near_left}
              visual_acuity_near_right={reportData.visual_acuity_near_right}
              visual_acuity_left={reportData.visual_acuity_left} // Backward compatibility
              visual_acuity_right={reportData.visual_acuity_right} // Backward compatibility
              vision_issues={reportData.vision_issues} // Keep for backward compatibility
            />
          </Box>
          <Box sx={{ padding: "1% 7.5%" }}>
            <InferenceDetailsComponent
              ref={inferenceRef}
              edit={(offline || reportData.id) && !isPendingSync} // Disable edit for pending sync records
              handleImageEditClick={handleImageEditClick}
              patientrecordId={patientrecordId}
              leftImage={thumbnailUrls.left}
              rightImage={thumbnailUrls.right}
              leftInference={handleSetDR(
                reportData?.ml_model?.classification?.left,
                reportData.ml_model?.gradability?.left,
                reportData?.ml_model?.model_outputs?.left
              )}
              rightInference={handleSetDR(
                reportData?.ml_model?.classification?.right,
                reportData?.ml_model?.gradability?.right,
                reportData?.ml_model?.model_outputs?.right
              )}
              ophthalmologistLeftInference={reportData?.feedback_data?.left_eye_correct_inference}
              ophthalmologistRightInference={reportData?.feedback_data?.right_eye_correct_inference}
              ophthalmologistLeftDME={reportData?.feedback_data?.left_eye_correct_dme}
              ophthalmologistRightDME={reportData?.feedback_data?.right_eye_correct_dme}
              required={handleSetReferral(
                // Use feedback-corrected inference if available, otherwise use AI inference (with fallback to DR_grade)
                reportData?.feedback_data?.left_eye_correct_inference ||
                getInferenceFromData(reportData?.ml_model?.classification?.left, reportData?.ml_model?.model_outputs?.left),
                reportData?.feedback_data?.right_eye_correct_inference ||
                getInferenceFromData(reportData?.ml_model?.classification?.right, reportData?.ml_model?.model_outputs?.right),
                reportData?.single_image_reason,
                reportData?.ml_model?.operator_override || { left: { fundus: false, eye: false }, right: { fundus: false, eye: false } },
                { left: getFinalDME('left'), right: getFinalDME('right') }
              )}
              nextStepsData={reportData?.feedback_data?.next_steps_message || reportData?.ml_model?.nextSteps}
              feedbackData={reportData?.feedback_data}
              leftModelScore={reportData?.ml_model?.classification?.left?.model_score}
              rightModelScore={reportData?.ml_model?.classification?.right?.model_score}
              singleImageReason={reportData?.single_image_reason}
              operatorOverride={reportData?.ml_model?.operator_override || { left: { fundus: false, eye: false }, right: { fundus: false, eye: false } }}
              modelOutputs={reportData?.ml_model?.model_outputs || { left: null, right: null }}
              onValidityChange={setCanSubmitFeedback}
            />
          </Box>
          {/* <Accordion defaultExpanded={true}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                padding: "0 7.5%",
                display: "flex",
                alignItems: "center",
                background: "#BDD7F21F",
              }}
            >
              <Typography fontWeight={600} fontSize="20px" color="#181818">
                Health Information
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: "0 7.5%" }}>
              <HealthDetailsComponent health_info={reportData.health_info} />
            </AccordionDetails>
          </Accordion> */}
          <Accordion defaultExpanded={true} sx={{ marginBottom: "60px"}}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                padding: "0 7.5%",
                display: "flex",
                alignContent: "center",
                background: "#BDD7F21F",
              }}
            >
              <Typography fontWeight={600} fontSize="20px" color="#181818">
                Disclaimer
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: "2% 7.5%", marginBottom: "60px" }}>
              <DisclaimerComponent />
            </AccordionDetails>
          </Accordion>
        </Box>
        
        <Box
    sx={{
      position: "fixed",
      bottom: 0,
      left: 0,
      width: "100%",
      bgcolor: "#fff",
      boxShadow: "0 -2px 6px rgba(0,0,0,0.1)",
      padding: "12px 16px",
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      zIndex: 1200,
    }}
  >
    {optometristRole === "Ophthalmologist" && !reportData?.feedback_data && (
      <Fab
        sx={{
          width: "150px",
          borderRadius: "8px",
          background: "#051572",
          color: "#FFFFFF",
          "&:hover": {
            background: "#051572",
          },
          "&.Mui-disabled": {
            background: "#ccc",
            color: "#888",
          },
          textTransform: "none",
          boxShadow: "none",
          mr: 3,
        }}
        disabled={!canSubmitFeedback}
        onClick={handleSubmit}
      >
        Submit Form <ArrowForwardIcon sx={{ ml: 1 }} />
      </Fab>
    )}
  </Box>

      </div>
      <LoadingComponent open={isLoading || imagesLoading} text={false} />
       {/* ReportButton fixed above the Submit button container, no background */}
       <Box
    sx={{
      position: "sticky",
      bottom: "90px", // exactly above the bottom white bar height
      width: "fit-content",
      marginLeft: "auto",
      marginRight: 2,
      zIndex: 1300,
    }}
  >
    <ReportButton reports={reports} />
  </Box>



      
      {/* Submit Feedback Modal */}
      <SubmitFeedbackModal
        open={showSubmitModal}
        onClose={cancelSubmit}
        onConfirm={confirmSubmit}
        formData={showSubmitModal ? inferenceRef.current?.getFormData() : null}
        reportNumber={currentReportIndex}
        leftInference={handleSetDR(
          reportData?.ml_model?.classification?.left,
          reportData.ml_model?.gradability?.left
        )}
        rightInference={handleSetDR(
          reportData?.ml_model?.classification?.right,
          reportData?.ml_model?.gradability?.right,
          reportData?.ml_model?.model_outputs?.right
        )}
        /* Deliberately the raw AI grade, NOT getFinalDME() - this modal is confirming what's
           about to be submitted, showing the AI's original grade next to the clinician's
           just-entered correction, not the final value. */
        leftDME={reportData?.ml_model?.model_outputs?.left?.drdme?.DME_grade || null}
        rightDME={reportData?.ml_model?.model_outputs?.right?.drdme?.DME_grade || null}
      />
      
      {/* Back Navigation Modal */}
      <BackNavigationModal
        open={showBackModal}
        onClose={handleCloseBackModal}
        onExitWithoutFeedback={handleExitWithoutFeedback}
        currentReportIndex={currentReportIndex}
      />
    </div>
  );
};

export default ReportsComponent;
